/**
 * The invite sheet's URL state, and the only file allowed to read it.
 *
 * The sheet is an overlay on the existing `/pools` route, opened as
 * `?sheet=invite`. Its open tab and its People-tab query text live in the
 * same two search params `sectionSearch.ts` already coerces, `inviteTab` and
 * `inviteQ`.
 *
 * The two reads that need a fallback live here, so no component re-derives
 * them: `search.sheet` against the sheet's name, and `search.inviteTab`
 * against the tab list. `search.inviteQ` is read inline at the one call site
 * that owns it, because a query string has no fallback to apply, only an
 * empty default.
 */
import { INVITE_TABS } from "../../routeState/sectionSearch";

export const INVITE_SHEET_NAME = "invite";

export type typeInviteTab = (typeof INVITE_TABS)[number];

export const DEFAULT_INVITE_TAB: typeInviteTab = "people";

/** True only when the invite sheet is the open sheet. */
export function isInviteSheetOpen(search: {
  sheet?: string | undefined;
}): boolean {
  return search.sheet === INVITE_SHEET_NAME;
}

/** The active tab, falling back to `people` when absent or unrecognized. */
export function readInviteTab(search: {
  inviteTab?: string | undefined;
}): typeInviteTab {
  const candidate = search.inviteTab;
  if (
    candidate !== undefined &&
    (INVITE_TABS as readonly string[]).includes(candidate)
  ) {
    return candidate as typeInviteTab;
  }
  return DEFAULT_INVITE_TAB;
}
